import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { mcpClassApi } from '@/api/mcpClass.api';
import type { Param } from '@/proto/agentplatform/v1/param_pb';

interface FormState {
  name: string;
  description: string;
  paramValues: Record<string, string>;
}

export default function CreateMCPInstanceFromClassPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);

  const { data: classData, isLoading } = useQuery({
    queryKey: ['mcp-class', id],
    queryFn: () => mcpClassApi.getMCPClass(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (classData && form === null) {
      setForm({
        name: '',
        description: '',
        paramValues: Object.fromEntries(
          (classData.params || []).map((p: Param) => [
            p.name,
            p.paramValue?.case === 'value' ? p.paramValue.value : '',
          ])
        ),
      });
    }
  }, [classData, form]);

  const createMutation = useMutation({
    mutationFn: (f: FormState) =>
      mcpClassApi.createMCPInstanceFromClass(id!, {
        name: f.name.trim(),
        description: f.description,
        params: f.paramValues,
      }),
    onSuccess: (mcp) => {
      queryClient.invalidateQueries({ queryKey: ['mcps'] });
      queryClient.invalidateQueries({ queryKey: ['mcps', 'by-class', id] });
      if (mcp.id) {
        navigate(`/plugins/mcps/instances/${mcp.id}`);
      } else {
        navigate(`/plugins/mcps/classes/${id}`);
      }
    },
  });

  if (isLoading || !form || !classData) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const paramDefs = classData.params || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/plugins/mcps/classes/${id}`)}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">从 MCP 模版创建实例</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              来源模版：<span className="font-medium">{classData.name}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/plugins/mcps/classes/${id}`)}>
            取消
          </Button>
          <Button
            disabled={!form.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(form)}
          >
            {createMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}创建实例
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1 self-start">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">基本信息</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">实例名称用于在列表中识别这个 MCP。</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">实例名称</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="如: my-github"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">描述</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">参数配置</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              覆盖 MCP 模版模板上的可配置参数；不填则使用类的默认值。
            </p>
          </CardHeader>
          <CardContent>
            {paramDefs.length === 0 ? (
              <div className="rounded-md border border-dashed bg-muted/20 px-4 py-10 text-center">
                <p className="text-sm text-muted-foreground">该 MCP 模版没有定义可配置参数。</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paramDefs.map((p: Param) => (
                  <div key={p.name} className="space-y-1.5">
                    <label className="text-xs text-muted-foreground flex items-center gap-2">
                      <span className="font-mono">{p.name}</span>
                      {p.target && (
                        <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded">
                          → {p.target}
                        </span>
                      )}
                    </label>
                    <Input
                      value={form.paramValues[p.name] || ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          paramValues: { ...form.paramValues, [p.name]: e.target.value },
                        })
                      }
                      className="h-9 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
